/**
 * Editor de bounding boxes da assinatura questionada (E4.F2b).
 *
 * Encapsula:
 * - O canvas Fabric.js editável (mover / redimensionar BBs já existentes).
 * - Auto-save com debounce 800ms — a cada `object:modified` agendamos um
 *   PATCH; eventos sucessivos resetam o timer (1 PATCH por burst).
 * - Indicador "Salvando…/Salvo/Erro" com `aria-live='polite'`.
 * - Botão "Restaurar marcações do sistema" (visível apenas quando há
 *   override persistido e a análise ainda não foi aprovada).
 *
 * Análise aprovada (`approved_at != null`) é imutável: canvas vira read-only
 * e o botão de restaurar some — qualquer PATCH retornaria 409.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Cloud, CloudOff, CloudUpload, RotateCcw } from 'lucide-react';
import { ptBR } from '@/i18n/pt-BR';
import { AnalysisCanvas } from '@/components/AnalysisCanvas';
import {
  AnalysisError,
  updateBoundingBoxes,
  type AnalysisRecordData,
  type CanvasBoundingBoxData,
} from '@/lib/analyses';
import { useApiClient } from '@/lib/api';

const DEBOUNCE_MS = 800;
const SAVED_VISIBLE_MS = 2000;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface BoundingBoxesEditorProps {
  record: AnalysisRecordData;
  imageUrl: string | null;
  altText: string;
  testId?: string;
}

function pickInitialBboxes(record: AnalysisRecordData): CanvasBoundingBoxData[] {
  if (record.bounding_boxes_override !== null && record.bounding_boxes_override !== undefined) {
    return record.bounding_boxes_override;
  }
  return record.result?.bounding_boxes ?? [];
}

function StatusIndicator({ status }: { status: SaveStatus }) {
  let Icon = Cloud;
  let label = '';
  let className = 'text-fg-muted';
  if (status === 'saving') {
    Icon = CloudUpload;
    label = ptBR.analise.salvandoEdicoes;
    className = 'text-accent-700';
  } else if (status === 'saved') {
    Icon = Cloud;
    label = ptBR.analise.edicoesSalvas;
    className = 'text-success-700';
  } else if (status === 'error') {
    Icon = CloudOff;
    label = ptBR.analise.erroSalvarEdicoes;
    className = 'text-danger-700';
  }
  return (
    <div
      data-testid="analise-save-status"
      data-status={status}
      role="status"
      aria-live="polite"
      className={`mt-2 flex items-center gap-2 text-caption ${className}`}
    >
      {label === '' ? null : (
        <>
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{label}</span>
        </>
      )}
    </div>
  );
}

export function BoundingBoxesEditor({
  record,
  imageUrl,
  altText,
  testId = 'analise-canvas-quest',
}: BoundingBoxesEditorProps) {
  const apiClient = useApiClient();
  const approved = record.approved_at !== null && record.approved_at !== undefined;
  const [bboxes, setBboxes] = useState<CanvasBoundingBoxData[]>(() => pickInitialBboxes(record));
  const [hasOverride, setHasOverride] = useState<boolean>(
    record.bounding_boxes_override !== null && record.bounding_boxes_override !== undefined,
  );
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [restoring, setRestoring] = useState(false);
  // Counter usado como `key` do canvas para forçar re-mount quando o
  // estado externo (restore) precisa atravessar o snapshot inicial. O
  // canvas deliberadamente não re-renderiza com mudanças subsequentes
  // de `boundingBoxes` (evita loop React ↔ Fabric).
  const [canvasVersion, setCanvasVersion] = useState(0);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const transitionToSaved = useCallback(() => {
    setStatus('saved');
    if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => {
      setStatus((s) => (s === 'saved' ? 'idle' : s));
    }, SAVED_VISIBLE_MS);
  }, []);

  const handleBoundingBoxesChange = useCallback(
    (next: CanvasBoundingBoxData[]) => {
      setBboxes(next);
      if (approved) return;
      if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        setStatus('saving');
        void (async () => {
          try {
            const updated = await updateBoundingBoxes(apiClient, record.id, next);
            setHasOverride(
              updated.bounding_boxes_override !== null &&
                updated.bounding_boxes_override !== undefined,
            );
            transitionToSaved();
          } catch (err) {
            // AnalysisError já é tratado por status='error'; classificações
            // são consumidas pelos testes via data-status. Retry implícito
            // ocorre no próximo `object:modified` (debounce reseta).
            void (err instanceof AnalysisError);
            setStatus('error');
          }
        })();
      }, DEBOUNCE_MS);
    },
    [apiClient, record.id, approved, transitionToSaved],
  );

  const handleRestore = useCallback(async () => {
    if (restoring || approved) return;
    setRestoring(true);
    setStatus('saving');
    try {
      const updated = await updateBoundingBoxes(apiClient, record.id, null);
      const restored = updated.result?.bounding_boxes ?? [];
      setBboxes(restored);
      setHasOverride(
        updated.bounding_boxes_override !== null && updated.bounding_boxes_override !== undefined,
      );
      setCanvasVersion((v) => v + 1);
      transitionToSaved();
    } catch (err) {
      void (err instanceof AnalysisError);
      setStatus('error');
    } finally {
      setRestoring(false);
    }
  }, [apiClient, record.id, restoring, approved, transitionToSaved]);

  return (
    <div>
      <AnalysisCanvas
        key={`canvas-${canvasVersion}`}
        imageUrl={imageUrl}
        boundingBoxes={bboxes}
        altText={altText}
        testId={testId}
        editable={!approved}
        onBoundingBoxesChange={approved ? undefined : handleBoundingBoxesChange}
      />
      {approved ? null : <StatusIndicator status={status} />}
      {hasOverride && !approved ? (
        <button
          type="button"
          onClick={() => void handleRestore()}
          disabled={restoring}
          aria-disabled={restoring}
          data-testid="analise-restaurar-marcacoes"
          className="mt-sc-2 inline-flex items-center gap-sc-2 border border-border px-sc-3 py-1.5 text-caption text-fg transition-colors duration-fast ease-sc hover:bg-fg/[0.07] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-45"
        >
          <RotateCcw className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {restoring
              ? ptBR.analise.restaurarMarcacoesEnviando
              : ptBR.analise.restaurarMarcacoesSistema}
          </span>
        </button>
      ) : null}
    </div>
  );
}
